/**
 * Absence over time — the first real report (DESIGN-reports.md step 0b, 23/09/2026).
 *
 * Days off each month by leave type, for the viewer's reach, over the leave year or
 * chosen dates (decision H). Every bar clicks through to the people behind it (J), and
 * the page and the clicked-open list both download (I). Counting is done on the server
 * with Payroll's own costing, so a half day is 0.5 and bank holidays and non-working days
 * cost nothing — this page only draws what it is given.
 *
 * ⚠️ Leave-type names arrive already decided by the server's disclosure rules: a private
 * type the viewer may not see comes as "Time off". Never look a name up here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, CartesianGrid,
} from 'recharts';
import { reportsApi, apiErrorMessage, saveBlobResponse } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import {
  ReportFrame, Card, Chip, PillSelect, PeriodPicker, DownloadMenu, PrintButton, NoAccess, fmtDays,
  ScrollTable,
} from '../../components/reports/ReportUi';

const PAGE_DOWNLOADS = [
  { value: 'xlsx', label: 'Excel (both sheets)' },
  { value: 'csv_months', label: 'CSV: by month' },
  { value: 'csv_people', label: 'CSV: people' },
];
const LIST_DOWNLOADS = [
  { value: 'csv_people', label: 'CSV' },
  { value: 'xlsx', label: 'Excel' },
];

const MonthTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const items = payload.filter((p) => (p.value || 0) > 0);
  const total = items.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="rounded-xl border border-hive-line bg-white dark:bg-card dark:border-border px-3 py-2 text-xs shadow">
      <div className="font-bold mb-1">{payload[0]?.payload?.label || label} — {fmtDays(total)} {total === 1 ? 'day' : 'days'}</div>
      {items.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 leading-5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span>{fmtDays(p.value)} {p.name}</span>
        </div>
      ))}
      <div className="text-hive-muted mt-1">Click to see who</div>
    </div>
  );
};

const AbsenceOverTimePage = () => {
  const [period, setPeriod] = useState({ year: null, start: null, end: null });
  const [filters, setFilters] = useState({ department_id: '', location_id: '', leave_type: '' });
  const [view, setView] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [drill, setDrill] = useState(null);          // { month, monthLabel, label }
  const [drillData, setDrillData] = useState(null);
  const [busy, setBusy] = useState(null);

  // What every call sends: the period, then whichever filters are set.
  const baseParams = useMemo(() => {
    const p = period.start && period.end ? { start: period.start, end: period.end }
      : (period.year ? { year: period.year } : {});
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [period, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.absence(baseParams);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [baseParams]);

  useEffect(() => { load(); }, [load]);
  // A new period or filter makes an open list stale — close it rather than show old names.
  useEffect(() => { setDrill(null); setDrillData(null); }, [baseParams]);

  useEffect(() => {
    if (!drill) return;
    let cancelled = false;
    (async () => {
      try {
        const params = { ...baseParams };
        if (drill.month) params.month = drill.month;
        if (drill.label) params.leave_type = drill.label;
        const res = await reportsApi.absencePeople(params);
        if (!cancelled) setDrillData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load the list'));
      }
    })();
    return () => { cancelled = true; };
  }, [drill, baseParams]);

  const download = async (fmt, extra = {}) => {
    setBusy(fmt);
    try {
      const res = await reportsApi.exportAbsence({ ...baseParams, ...extra, format: fmt });
      const name = saveBlobResponse(res, `absence-over-time.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      let message = 'Could not build the download';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  const summary = data?.summary;
  const types = summary?.types || [];
  // Series keys are t_0, t_1… rather than the type names, so a type called "total" or
  // "label" can never collide with the row's own fields.
  const chartRows = (summary?.months || []).map((m) => {
    const row = { name: m.month, key: m.key, label: m.label, future: m.future, total: m.total, top: -1 };
    types.forEach((t, i) => {
      row[`t_${i}`] = m.by_type[t.name] || 0;
      if (row[`t_${i}`] > 0) row.top = i;       // the highest series with any days: carries the total
    });
    return row;
  });

  const openRow = (row) => {
    if (row && row.total > 0) setDrill({ month: row.key, monthLabel: row.label, label: filters.leave_type || null });
  };
  // Clicking a coloured block: recharts hands the block's row over as `payload`.
  const openBar = (entry) => openRow(chartRows.find((r) => r.key === (entry?.payload?.key || entry?.key)));
  // Clicking the empty space above a short bar: fall back to the column under the pointer.
  const openMonth = (state) => {
    const idx = Number(state?.activeTooltipIndex);
    if (Number.isInteger(idx)) openRow(chartRows[idx]);
  };
  // The total sits on top of whichever series is highest in that month, so every bar gets
  // one — not only the months that happen to contain the last leave type.
  // ⚠️ Read the row from the BLOCK (`entry.payload`), never from the label's index: recharts
  // skips empty blocks, so the index drifts and the totals landed two months to the right
  // (seen on Rob's data, 23/09/2026).
  const totalFor = (seriesIndex) => (entry) =>
    (entry?.payload?.top === seriesIndex && entry.payload.total ? entry.payload.total : null);
  const TotalLabel = ({ x, y, width, value }) => (value == null ? null : (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11.5} fontWeight={800} fill="#6B6255">
      {fmtDays(value)}
    </text>
  ));

  if (forbidden) return <ReportFrame><NoAccess /></ReportFrame>;

  const monthTypes = drill?.month
    ? types.filter((t) => (summary.months.find((m) => m.key === drill.month)?.by_type[t.name] || 0) > 0)
    : [];

  return (
    <ReportFrame>
      <p className="text-[13px] text-hive-muted dark:text-muted-foreground mb-1 print:hidden">
        <Link to="/reports" className="hover:underline">Reports</Link> › <b className="text-hive-navy dark:text-foreground">Absence over time</b>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">Absence over time</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? `${data.scope.label} · ${data.period.label}${data.period.range ? ` (${data.period.range})` : ''}` : 'Loading…'}
          </p>
          {/* Rob's f (12/10/2026): sickness is health data, so without "View sickness" it is left
              out here — and the page says so, rather than letting the totals look complete. */}
          {data?.sickness_hidden && (
            <p className="text-xs text-hive-muted dark:text-muted-foreground mt-0.5" data-testid="absence-sickness-hidden">
              Sickness isn't included. It's health information, shown only to people with "View sickness".
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <PeriodPicker
              period={data.period}
              onYear={(y) => setPeriod({ year: y, start: null, end: null })}
              onDates={(s, e) => setPeriod({ year: null, start: s, end: e })}
            />
          )}
          <PrintButton />
          <DownloadMenu options={PAGE_DOWNLOADS} busy={!!busy} onPick={(fmt) => download(fmt)} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 print:hidden">
        <PillSelect label="Department" value={filters.department_id} onChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <option value="">All departments</option>
          {(data?.filters.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </PillSelect>
        <PillSelect label="Location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
          <option value="">All locations</option>
          {(data?.filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </PillSelect>
        <PillSelect label="Leave type" value={filters.leave_type} onChange={(v) => setFilters((f) => ({ ...f, leave_type: v }))}>
          <option value="">All leave types</option>
          {(data?.filters.leave_types || []).map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
        </PillSelect>
        <span className="flex-1" />
        <span className="inline-flex rounded-full border border-hive-line dark:border-border bg-hive-cream dark:bg-card p-[3px]">
          {[['month', 'By month'], ['type', 'By leave type']].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              className={`rounded-full px-3 py-1 text-[12.5px] font-extrabold ${view === k ? 'bg-white dark:bg-muted text-hive-navy dark:text-foreground shadow' : 'text-hive-muted'}`}
            >
              {l}
            </button>
          ))}
        </span>
      </div>

      <div className={`grid lg:grid-cols-3 gap-3.5 mt-3.5 ${loading ? 'opacity-60' : ''}`}>
        <Card className="lg:col-span-2">
          {view === 'month' ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-poppins font-semibold text-[17px]">Days off each month</p>
                <div className="flex flex-wrap gap-3 text-[12.5px] font-bold text-hive-muted dark:text-muted-foreground">
                  {types.map((t) => (
                    <span key={t.name} className="inline-flex items-center gap-1.5">
                      <i className="inline-block w-[11px] h-[11px] rounded-[3px]" style={{ background: t.color }} />{t.name}
                    </span>
                  ))}
                </div>
              </div>
              {/* hh-report-chart: no focus box round the chart on a mouse click (index.css) */}
              <div style={{ width: '100%', height: 260 }} className="mt-2 hh-report-chart">
                <ResponsiveContainer>
                  <BarChart data={chartRows} margin={{ top: 22, right: 6, left: -18, bottom: 0 }}
                            onClick={openMonth} style={{ cursor: 'pointer' }}>
                    <CartesianGrid vertical={false} stroke="#F3E9DA" strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B6255' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6B6255' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(247,135,31,.08)' }} content={<MonthTooltip />} />
                    {types.map((t, i) => (
                      <Bar key={t.name} dataKey={`t_${i}`} name={t.name} stackId="off" fill={t.color}
                           onClick={openBar} style={{ cursor: 'pointer' }}
                           radius={i === types.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}>
                        {chartRows.map((r) => (
                          <Cell key={r.key} fillOpacity={r.future ? 0.45 : 1}
                                stroke={drill?.month === r.key ? '#131F3C' : undefined}
                                strokeWidth={drill?.month === r.key ? 1.5 : 0} />
                        ))}
                        <LabelList valueAccessor={totalFor(i)} content={TotalLabel} />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-hive-muted dark:text-muted-foreground mt-2">
                Faded bars are leave already booked for later. Click any bar to see who it is.
              </p>
            </>
          ) : (
            <>
              <p className="font-poppins font-semibold text-[17px] mb-3">Days off by leave type</p>
              {types.length === 0 && <p className="text-sm text-hive-muted py-6 text-center">No approved leave in this period</p>}
              <div className="space-y-2.5">
                {types.map((t) => {
                  const max = Math.max(1, ...types.map((x) => x.days));
                  return (
                    <button key={t.name} onClick={() => setDrill({ month: null, monthLabel: null, label: t.name })}
                            className="w-full text-left group">
                      <div className="flex justify-between text-sm font-bold">
                        <span>{t.name}</span>
                        <span className="text-hive-muted">{fmtDays(t.days)} days · {t.people} {t.people === 1 ? 'person' : 'people'}</span>
                      </div>
                      <div className="h-3 rounded-full bg-hive-tile dark:bg-muted mt-1 overflow-hidden">
                        <div className="h-3 rounded-full group-hover:opacity-80" style={{ width: `${(100 * t.days) / max}%`, background: t.color }} />
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-hive-muted dark:text-muted-foreground mt-3">Click a leave type to see who.</p>
            </>
          )}
        </Card>

        <Card>
          <p className="font-poppins font-semibold text-[17px] mb-1.5">
            {data?.period.kind === 'custom' ? 'These dates' : 'This leave year'}
          </p>
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-hive-muted dark:text-muted-foreground text-[12.5px]">
                <th className="py-2 font-bold">Leave type</th>
                <th className="py-2 font-bold text-right">Days</th>
                <th className="py-2 font-bold text-right">People</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.name} className="border-t border-hive-line-softer dark:border-border cursor-pointer hover:bg-hive-cream dark:hover:bg-muted"
                    onClick={() => setDrill({ month: null, monthLabel: null, label: t.name })}>
                  <td className="py-2"><span className="inline-block w-1 h-4 rounded mr-2 align-middle" style={{ background: t.color }} />{t.name}</td>
                  <td className="py-2 text-right tabular-nums">{fmtDays(t.days)}</td>
                  <td className="py-2 text-right tabular-nums">{t.people}</td>
                </tr>
              ))}
              <tr className="border-t border-hive-line dark:border-border font-bold">
                <td className="py-2">Total</td>
                <td className="py-2 text-right tabular-nums">{fmtDays(summary?.total_days)}</td>
                <td className="py-2 text-right tabular-nums">{summary?.people_with_absence ?? ''}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-sm text-hive-muted dark:text-muted-foreground mt-2.5">
            Counted the way balances are: each person's own working week, their bank holidays don't count, and a half day is 0.5.
          </p>
        </Card>
      </div>

      {drill && (
        <div className="mt-3.5 rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card p-4" data-testid="drill">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-poppins font-semibold text-base">
                {[drill.monthLabel || (data?.period.label), drill.label].filter(Boolean).join(' · ')}
                {drillData && ` · ${fmtDays(drillData.total_days)} days · ${drillData.people} ${drillData.people === 1 ? 'person' : 'people'}`}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {drill.month && (
                  <>
                    <button onClick={() => setDrill((d) => ({ ...d, label: null }))}>
                      <Chip tone={!drill.label ? 'pending' : 'grey'}>All leave types</Chip>
                    </button>
                    {monthTypes.map((t) => (
                      <button key={t.name} onClick={() => setDrill((d) => ({ ...d, label: t.name }))}>
                        <Chip tone={drill.label === t.name ? 'pending' : 'grey'}>{t.name}</Chip>
                      </button>
                    ))}
                  </>
                )}
                <DownloadMenu small label="Download this list" options={LIST_DOWNLOADS} busy={!!busy}
                              onPick={(fmt) => download(fmt, {
                                ...(drill.month ? { month: drill.month } : {}),
                                ...(drill.label ? { leave_type: drill.label } : {}),
                              })} />
              </div>
            </div>
            <button aria-label="Close the list" onClick={() => { setDrill(null); setDrillData(null); }} className="text-hive-muted hover:text-hive-navy print:hidden">
              <X className="w-5 h-5" />
            </button>
          </div>
          <ScrollTable className="mt-2">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-left text-hive-muted dark:text-muted-foreground text-[12.5px]">
                  <th className="py-2 font-bold">Person</th>
                  <th className="py-2 font-bold">Department</th>
                  <th className="py-2 font-bold">Leave type</th>
                  <th className="py-2 font-bold">Dates</th>
                  {!drill.month && <th className="py-2 font-bold">Month</th>}
                  <th className="py-2 font-bold text-right">Days</th>
                </tr>
              </thead>
              <tbody>
                {!drillData && <tr><td colSpan={6} className="py-4 text-hive-muted">Loading…</td></tr>}
                {drillData && drillData.rows.length === 0 && <tr><td colSpan={6} className="py-4 text-hive-muted">Nobody</td></tr>}
                {(drillData?.rows || []).map((r, i) => (
                  <tr key={`${r.user_id}-${r.month}-${r.from}-${i}`} className="border-t border-hive-line-softer dark:border-border">
                    <td className="py-2">
                      <span className={`inline-flex w-[26px] h-[26px] rounded-full text-white text-[11px] font-extrabold items-center justify-center mr-2 align-middle ${r.former || r.left ? 'bg-hive-disabled' : 'bg-gradient-to-br from-hive-orange to-hive-orange-light'}`}>
                        {r.former ? '–' : r.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                      {r.name} {r.left && <Chip>Left</Chip>}
                    </td>
                    <td className="py-2">{r.department}</td>
                    <td className="py-2"><span className="inline-block w-1 h-4 rounded mr-2 align-middle" style={{ background: r.color }} />{r.leave_type}</td>
                    <td className="py-2 whitespace-nowrap">{formatDateGB(r.from)}{r.to !== r.from ? ` – ${formatDateGB(r.to)}` : ''}</td>
                    {!drill.month && <td className="py-2 whitespace-nowrap">{r.month_label}</td>}
                    <td className="py-2 text-right tabular-nums">{fmtDays(r.days)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollTable>
        </div>
      )}
    </ReportFrame>
  );
};

export default AbsenceOverTimePage;
